import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Search, Eye, Check, Download, X } from "lucide-react";
import { supabase } from "../supabaseClient";
import AdminTopBar from "./AdminTopBar";
import AdminPagination from "./AdminPagination";
import { BusyTable } from "../components/TableBusyOverlay";
import usePagedListUi from "../hooks/usePagedListUi";
import { ADMIN_PAGE_SIZE, applyPagedSelect, totalPagesFromCount, uniqueIds } from "../lib/adminPagination";
import {
  adminCard,
  adminEmpty,
  adminPageBg,
  adminPageTitle,
  adminPrimaryBtn,
  adminRow,
  adminSearchInput,
  adminSecondaryBtn,
  adminStatusPill,
  adminTableHead,
  adminTableWrap,
  adminTd,
  adminTdName,
} from "./adminUi";
import TicketReceiptCard, { formatManualTicketId } from "../components/TicketReceiptCard";
import SuccessToast from "../components/SuccessToast";
import { formatExactSection, resolveStoredSeatArea } from "../lib/seatSectionDisplay";
import { buildTicketReceiptPdf } from "../lib/ticketReceiptPdf";
import { formatManilaDateTime } from "../lib/dateTime";
import { resolveEventPolicyHtml } from "../lib/venuePolicyHtml";
import { resolveTicketSectionColor } from "../lib/sectionColor";
import { fetchVenueSectionColors } from "../lib/venuesApi";
import { defaultLast30ManilaDays } from "../lib/adminOrdersReport";
import { buildOrderCode } from "./ordersPage";
import SearchableSelect from "./SearchableSelect";
import {
  PDF_DETAIL_ROW_LIMIT,
  buildTicketsCsv,
  buildTicketsReportPdf,
  downloadTextFile,
  filterTicketsForExport,
  summarizeTickets,
  ticketsReportFilename,
} from "../lib/adminTicketsReport";

const PAGE_SIZE = ADMIN_PAGE_SIZE;

const formatOrderCode = (num) => `ORD-${String(num).padStart(3, "0")}`;

const formatDateTime = formatManilaDateTime;

const getSecondKey = (value) => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value || "unknown");
  parsed.setMilliseconds(0);
  return parsed.toISOString();
};

const ticketIdFromSearchRow = (row) => {
  if (typeof row === "string") return row;
  if (!row || typeof row !== "object") return "";
  return row.admin_search_ticket_ids || row.id || "";
};

const isMissingColumnError = (error, column) => {
  if (!error) return false;
  return String(error.code || "") === "42703" && String(error.message || "").toLowerCase().includes(column);
};

const rememberTicketIds = (ids, rows) => {
  (rows || []).forEach((row) => {
    if (row?.id) ids.add(row.id);
  });
};

const searchTicketIdsFallback = async (term) => {
  const like = `%${term}%`;
  const ids = new Set();
  let succeeded = false;

  const attendee = await supabase.from("tickets").select("id").ilike("attendee_name", like).limit(250);
  if (!attendee.error) {
    succeeded = true;
    rememberTicketIds(ids, attendee.data);
  } else if (!isMissingColumnError(attendee.error, "attendee_name")) {
    console.error("Ticket attendee search failed:", attendee.error);
  }

  const [usersResult, eventsResult] = await Promise.all([
    supabase.from("users").select("id").or(`full_name.ilike.${like},email.ilike.${like}`).limit(80),
    supabase.from("events").select("id").ilike("title", like).limit(80),
  ]);

  if (!usersResult.error) {
    succeeded = true;
    const userIds = uniqueIds(usersResult.data, "id");
    if (userIds.length) {
      const byBuyer = await supabase.from("tickets").select("id").in("buyer_id", userIds).limit(250);
      if (!byBuyer.error) rememberTicketIds(ids, byBuyer.data);
      else console.error("Ticket buyer search failed:", byBuyer.error);
    }
  } else {
    console.error("Buyer name search failed:", usersResult.error);
  }

  if (!eventsResult.error) {
    succeeded = true;
    const eventIds = uniqueIds(eventsResult.data, "id");
    if (eventIds.length) {
      const tiers = await supabase.from("ticket_tiers").select("id").in("event_id", eventIds);
      const tierIds = uniqueIds(tiers.data, "id");
      if (!tiers.error && tierIds.length) {
        const byEvent = await supabase.from("tickets").select("id").in("ticket_tier_id", tierIds).limit(250);
        if (!byEvent.error) rememberTicketIds(ids, byEvent.data);
        else console.error("Ticket event search failed:", byEvent.error);
      }
    }
  } else {
    console.error("Event title search failed:", eventsResult.error);
  }

  return { ok: succeeded, ids: [...ids].slice(0, 250) };
};

const Tickets = () => {
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [dataError, setDataError] = useState("");

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [eventFilter, setEventFilter] = useState("All Events");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [statusConfirm, setStatusConfirm] = useState(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [eventCatalog, setEventCatalog] = useState([]);
  const listTopRef = useRef(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportEvent, setExportEvent] = useState("");
  const [exportFrom, setExportFrom] = useState("");
  const [exportTo, setExportTo] = useState("");
  const [useIssuedRange, setUseIssuedRange] = useState(false);
  const [exportFormat, setExportFormat] = useState("csv");
  const [toastMessage, setToastMessage] = useState("");
  const [errorToastMessage, setErrorToastMessage] = useState("");

  const showSuccessToast = (message) => {
    setToastMessage(message);
    window.setTimeout(() => setToastMessage(""), 2500);
  };

  const showErrorToast = (message) => {
    setErrorToastMessage(message);
    window.setTimeout(() => setErrorToastMessage(""), 3500);
  };

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    const ticketsSelectBaseCols =
      "id, qr_code, buyer_id, ticket_tier_id, created_at, is_validated, validated_at, seat_label, seat_area";

    const eventsResult = await supabase
      .from("events")
      .select("id, title, date, time, venue, address, venue_policy_snapshot, description, venue_id, seat_layout_snapshot")
      .order("date", { ascending: false });

    let eventsData = eventsResult.data;
    let eventsError = eventsResult.error;
    if (eventsError) {
      const fallbackEvents = await supabase.from("events").select("id, title, date, time, venue");
      eventsData = fallbackEvents.data;
      eventsError = fallbackEvents.error;
    }
    if (!eventsError) setEventCatalog(eventsData || []);

    const eventIdForFilter = eventFilter === "All Events"
      ? ""
      : String((eventsData || []).find((event) => event.title === eventFilter)?.id || "");

    let eventTierIds = [];
    if (eventIdForFilter) {
      const { data: eventTiers } = await supabase.from("ticket_tiers").select("id").eq("event_id", eventIdForFilter);
      eventTierIds = uniqueIds(eventTiers, "id");
    }

    const needle = debouncedSearch.trim();
    let searchTicketIds = null;
    if (needle) {
      const { data: matchedIds, error: searchError } = await supabase.rpc("admin_search_ticket_ids", {
        p_term: needle,
      });
      if (searchError) {
        console.error("Ticket search function failed:", searchError);
        const fallback = await searchTicketIdsFallback(needle);
        if (!fallback.ok) {
          showErrorToast("Search is unavailable right now. Please try again.");
          setTickets([]);
          setTotalCount(0);
          setLoading(false);
          return;
        }
        searchTicketIds = fallback.ids;
      } else {
        searchTicketIds = (matchedIds || []).map(ticketIdFromSearchRow).filter(Boolean);
      }
      if (!searchTicketIds.length) {
        setTickets([]);
        setTotalCount(0);
        setLoading(false);
        return;
      }
    }

    const applyTicketFilters = (query) => {
      let next = query;
      if (statusFilter === "Used") next = next.eq("is_validated", true).is("refunded_at", null);
      else if (statusFilter === "Unused") next = next.eq("is_validated", false).is("refunded_at", null);
      else if (statusFilter === "Refunded") next = next.not("refunded_at", "is", null);
      if (eventTierIds.length) next = next.in("ticket_tier_id", eventTierIds);
      else if (eventIdForFilter) next = next.in("ticket_tier_id", ["00000000-0000-0000-0000-000000000000"]);
      if (searchTicketIds) next = next.in("id", searchTicketIds);
      return applyPagedSelect(next, currentPage, PAGE_SIZE);
    };

    const runTicketSelect = async (cols) => applyTicketFilters(
      supabase.from("tickets").select(cols, { count: "exact" }).order("created_at", { ascending: false })
    );

    let optionalCols = ["payment_order_id", "attendee_name", "refunded_at"];
    let ticketsResult = await runTicketSelect([ticketsSelectBaseCols, ...optionalCols].join(", "));
    while (
      ticketsResult.error &&
      String(ticketsResult.error.code || "") === "42703" &&
      optionalCols.length
    ) {
      const message = String(ticketsResult.error.message || "").toLowerCase();
      const missing = optionalCols.find((col) => message.includes(col)) || optionalCols[0];
      optionalCols = optionalCols.filter((col) => col !== missing);
      ticketsResult = await runTicketSelect([ticketsSelectBaseCols, ...optionalCols].join(", "));
    }

    const { data: ticketsData, error: ticketsError, count } = ticketsResult;
    const tierIds = uniqueIds(ticketsData, "ticket_tier_id");
    const buyerIds = uniqueIds(ticketsData, "buyer_id");

    let tiersResult = tierIds.length
      ? await supabase.from("ticket_tiers").select("id, event_id, name, price, section, seat_areas, color").in("id", tierIds)
      : { data: [], error: null };
    let { data: tiersData, error: tiersError } = tiersResult;
    if (
      tiersError &&
      (String(tiersError.code || "") === "42703" ||
        String(tiersError.message || "").toLowerCase().includes("color"))
    ) {
      const fallbackTiers = await supabase.from("ticket_tiers").select("id, event_id, name, price, section, seat_areas").in("id", tierIds);
      tiersData = fallbackTiers.data;
      tiersError = fallbackTiers.error;
    }
    if (
      tiersError &&
      (String(tiersError.code || "") === "42703" ||
        String(tiersError.message || "").toLowerCase().includes("seat_areas"))
    ) {
      const fallbackTiers = await supabase.from("ticket_tiers").select("id, event_id, name, price, section").in("id", tierIds);
      tiersData = fallbackTiers.data;
      tiersError = fallbackTiers.error;
    }

    const { data: usersData, error: usersError } = buyerIds.length
      ? await supabase.from("users").select("id, full_name, email").in("id", buyerIds)
      : { data: [], error: null };

    if (ticketsError || tiersError || eventsError) {
      const blockingError = ticketsError || tiersError || eventsError;
      console.error("Failed to fetch tickets:", blockingError);
      setDataError(`Ticket data could not be loaded: ${blockingError?.message || "Unknown error"}`);
      setTickets([]);
      setTotalCount(0);
      setLoading(false);
      return;
    }

    if (usersError) {
      console.warn("Users lookup blocked while loading tickets:", usersError);
      setDataError(`Limited data access: ${usersError.message}. Tickets are shown, attendee names may be unavailable.`);
    } else {
      setDataError("");
    }

    const tiersById = {};
    (tiersData || []).forEach((tier) => {
      tiersById[tier.id] = tier;
    });

    const eventsById = {};
    (eventsData || []).forEach((event) => {
      eventsById[event.id] = event;
    });

    const venueSections = await fetchVenueSectionColors(
      supabase,
      uniqueIds(eventsData, "venue_id")
    );

    const usersById = {};
    (usersData || []).forEach((user) => {
      usersById[user.id] = user;
    });

    const orderMap = new Map();
    let orderCounter = 1 + (currentPage - 1) * PAGE_SIZE;

    const rows = (ticketsData || []).map((ticket) => {
      const tier = tiersById[ticket.ticket_tier_id];
      const event = eventsById[tier?.event_id];
      const buyer = usersById[ticket.buyer_id];

      const orderKey = ticket.payment_order_id
        ? `order|${ticket.payment_order_id}`
        : `${ticket.buyer_id || "guest"}|${tier?.event_id || "event"}|${getSecondKey(ticket.created_at)}`;
      if (!orderMap.has(orderKey)) {
        if (ticket.payment_order_id) {
          orderMap.set(orderKey, buildOrderCode(ticket.payment_order_id));
        } else {
          orderMap.set(orderKey, formatOrderCode(orderCounter));
          orderCounter += 1;
        }
      }

      const status = ticket.refunded_at
        ? "Refunded"
        : ticket.is_validated
          ? "Used"
          : "Unused";

      return {
        id: ticket.id,
        ticketId: formatManualTicketId(ticket.id),
        orderCode: orderMap.get(orderKey),
        eventTitle: event?.title || "Unknown Event",
        attendee: String(ticket.attendee_name || "").trim() || buyer?.full_name || buyer?.email || "Guest",
        attendeeEmail: buyer?.email || "-",
        type: tier?.name || "General Admission",
        exactSection: formatExactSection(
          tier?.section || tier?.name || "",
          resolveStoredSeatArea({
            seatArea: ticket.seat_area,
            seatLabel: ticket.seat_label,
            tierSeatAreas: tier?.seat_areas,
          })
        ),
        sectionColor: resolveTicketSectionColor({
          tier,
          event,
          venueSections,
        }),
        price: Number(tier?.price || 0),
        status,
        scannedAt: ticket.validated_at || "",
        purchasedAt: ticket.created_at,
        eventDate: event?.date || "",
        eventTime: event?.time || "",
        venue: event?.venue || "-",
        eventAddress: event?.address || "",
        policyHtml: resolveEventPolicyHtml(event),
        seatLabel: ticket.seat_label || "",
        qrCode: ticket.qr_code || "",
      };
    });

    setTickets(rows);
    setTotalCount(Number(count) || rows.length);
    setLoading(false);
  }, [currentPage, eventFilter, debouncedSearch, statusFilter]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search), 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    fetchTickets();

    const channel = supabase
      .channel("tickets-admin-live")
      .on("postgres_changes", { event: "*", schema: "public", table: "tickets" }, fetchTickets)
      .on("postgres_changes", { event: "*", schema: "public", table: "ticket_tiers" }, fetchTickets)
      .on("postgres_changes", { event: "*", schema: "public", table: "events" }, fetchTickets)
      .on("postgres_changes", { event: "*", schema: "public", table: "users" }, fetchTickets)
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [fetchTickets]);

  const eventOptions = useMemo(() => {
    const unique = new Set(eventCatalog.map((item) => item.title).filter(Boolean));
    return ["All Events", ...Array.from(unique)];
  }, [eventCatalog]);

  const ticketsInView = useMemo(() => summarizeTickets(tickets), [tickets]);

  const exportCandidateEvents = useMemo(
    () => eventOptions.filter((name) => name !== "All Events"),
    [eventOptions]
  );

  const totalPages = totalPagesFromCount(totalCount, PAGE_SIZE);
  const paginatedTickets = tickets;
  const { showSkeleton, goToPage } = usePagedListUi({
    currentPage,
    setCurrentPage,
    loading,
    scrollTargetRef: listTopRef,
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, statusFilter, eventFilter]);

  const toggleSelectAll = () => {
    if (selectedIds.size === paginatedTickets.length && paginatedTickets.length > 0) {
      setSelectedIds(new Set());
      return;
    }

    setSelectedIds(new Set(paginatedTickets.map((item) => item.id)));
  };

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const confirmTicketIds = useMemo(() => {
    if (!statusConfirm?.ids?.length) return [];
    const idSet = new Set(statusConfirm.ids);
    return tickets
      .filter((ticket) => idSet.has(ticket.id))
      .map((ticket) => ticket.ticketId)
      .filter(Boolean);
  }, [statusConfirm, tickets]);

  const openStatusConfirm = (status, ids, mode) => {
    const uniqueIds = [...new Set(ids)].filter((id) => id !== undefined && id !== null && id !== "");
    if (!uniqueIds.length) return;
    setStatusConfirm({ status, ids: uniqueIds, mode });
  };

  const applyBulkStatus = async (status, ids = Array.from(selectedIds)) => {
    if (!ids.length) return;

    const scannedAt = status === "Used" ? new Date().toISOString() : null;

    const { error } = await supabase
      .from("tickets")
      .update({
        is_validated: status === "Used",
        validated_at: scannedAt,
      })
      .in("id", ids);

    if (error) {
      console.error("Failed to update ticket validation status:", error);
      return;
    }

    setTickets((prev) =>
      prev.map((ticket) =>
        ids.includes(ticket.id)
          ? {
              ...ticket,
              status,
              scannedAt: scannedAt || "",
            }
          : ticket
      )
    );
    setSelectedIds(new Set());
  };

  const confirmStatusChange = async () => {
    if (!statusConfirm) return;
    setIsUpdatingStatus(true);
    try {
      if (statusConfirm.mode === "one") {
        await applyOneStatus(statusConfirm.ids[0], statusConfirm.status);
      } else {
        await applyBulkStatus(statusConfirm.status, statusConfirm.ids);
      }
      setStatusConfirm(null);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const applyOneStatus = async (id, status) => {
    const scannedAt = status === "Used" ? new Date().toISOString() : null;

    const { error } = await supabase
      .from("tickets")
      .update({
        is_validated: status === "Used",
        validated_at: scannedAt,
      })
      .eq("id", id);

    if (error) {
      console.error("Failed to update ticket validation status:", error);
      return;
    }

    setTickets((prev) =>
      prev.map((ticket) =>
        ticket.id === id
          ? {
              ...ticket,
              status,
              scannedAt: scannedAt || "",
            }
          : ticket
      )
    );
  };

  const handleDownloadReceipt = async () => {
    if (!selectedTicket) return;

    const doc = await buildTicketReceiptPdf(selectedTicket);
    doc.save(`ticket-${selectedTicket.ticketId}.pdf`);
  };

  const openExportModal = () => {
    const prefillEvent = eventFilter !== "All Events" ? eventFilter : "";
    setExportEvent(prefillEvent);
    const range = defaultLast30ManilaDays();
    setExportFrom(range.from);
    setExportTo(range.to);
    setUseIssuedRange(false);
    setExportFormat("csv");
    setShowExportModal(true);
  };

  const closeExportModal = () => {
    setShowExportModal(false);
  };

  const downloadTicketsReport = () => {
    const eventTitle = String(exportEvent || "").trim();
    if (!eventTitle) {
      showErrorToast("Choose an event before exporting.");
      return;
    }

    let fromKey = "";
    let toKey = "";
    if (useIssuedRange) {
      fromKey = exportFrom;
      toKey = exportTo;
      if (!fromKey || !toKey) {
        showErrorToast("Choose issued from and to dates, or turn off the issued-date filter.");
        return;
      }
      if (fromKey > toKey) {
        const swapped = fromKey;
        fromKey = toKey;
        toKey = swapped;
        setExportFrom(fromKey);
        setExportTo(toKey);
      }
    }

    const needle = search.trim().toLowerCase();
    const statusAndSearch = tickets.filter((item) => {
      const matchStatus = statusFilter === "All" || item.status === statusFilter;
      const matchSearch =
        !needle ||
        item.ticketId.toLowerCase().includes(needle) ||
        item.orderCode.toLowerCase().includes(needle) ||
        item.attendee.toLowerCase().includes(needle) ||
        String(item.attendeeEmail || "").toLowerCase().includes(needle) ||
        item.eventTitle.toLowerCase().includes(needle) ||
        String(item.exactSection || "").toLowerCase().includes(needle);
      return matchStatus && matchSearch;
    });

    const rows = filterTicketsForExport(statusAndSearch, {
      eventTitle,
      fromKey,
      toKey,
    });
    const summary = summarizeTickets(rows);
    const filename = ticketsReportFilename(eventTitle, fromKey, toKey, exportFormat);

    try {
      if (exportFormat === "pdf") {
        const doc = buildTicketsReportPdf({
          tickets: rows,
          summary,
          eventTitle,
          fromKey,
          toKey,
        });
        doc.save(filename);
        if (rows.length > PDF_DETAIL_ROW_LIMIT) {
          showSuccessToast("PDF summary downloaded. Use CSV for the full door list.");
        } else {
          showSuccessToast("Tickets PDF downloaded.");
        }
      } else {
        const csv = buildTicketsCsv({
          tickets: rows,
          summary,
          eventTitle,
          fromKey,
          toKey,
        });
        downloadTextFile(csv, filename);
        showSuccessToast("Tickets CSV downloaded.");
      }
      setShowExportModal(false);
    } catch (error) {
      console.error("Unable to export tickets report:", error);
      showErrorToast("Unable to export this report. Please try again.");
    }
  };

  return (
    <div className={`${adminPageBg} text-gray-900`}>
      <AdminTopBar />

      <div className="p-4 sm:p-6 md:p-10">
        <div className="max-w-7xl mx-auto min-w-0">
        <SuccessToast message={toastMessage} />
        <SuccessToast message={errorToastMessage} variant="error" />

        <div className="mb-5">
          <h1 className={adminPageTitle}>Tickets</h1>
          <p className="text-xs text-gray-500 mt-1">
            In view: {ticketsInView.issuedCount} ticket{ticketsInView.issuedCount === 1 ? "" : "s"} · {ticketsInView.unusedCount} unused
          </p>
        </div>

        {dataError && (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
            {dataError}
          </div>
        )}

        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between mb-4">
          <div className="flex flex-wrap gap-2 w-full sm:w-auto">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-9 rounded-lg border border-gray-300 px-3 text-xs bg-white text-gray-800 w-full sm:w-auto"
            >
              <option value="All">All</option>
              <option value="Used">Used</option>
              <option value="Unused">Unused</option>
              <option value="Refunded">Refunded</option>
            </select>

            <SearchableSelect
              value={eventFilter}
              onChange={setEventFilter}
              options={eventOptions}
              pinnedOption="All Events"
              searchPlaceholder="Search events..."
              emptyLabel="No events found"
              className="w-full sm:w-auto sm:min-w-[220px]"
            />
          </div>

          <div className="relative w-full sm:w-[240px] min-w-0">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search ticket, order, attendee, email, event..."
              className={adminSearchInput}
            />
          </div>
        </div>

        <p className="text-xs text-gray-600 mb-4">Select tickets to run bulk actions, or export a door list for one event.</p>

        <div className="flex flex-wrap justify-end gap-2 mb-3">
          <button
            type="button"
            onClick={() => openStatusConfirm("Used", Array.from(selectedIds), "bulk")}
            className={`${adminSecondaryBtn} h-8 px-3`}
          >
            Mark Used
          </button>
          <button
            type="button"
            onClick={() => openStatusConfirm("Unused", Array.from(selectedIds), "bulk")}
            className={`${adminSecondaryBtn} h-8 px-3`}
          >
            Mark Unused
          </button>
          <button
            type="button"
            onClick={openExportModal}
            className={`${adminSecondaryBtn} h-8 px-3 gap-1.5`}
          >
            <Download size={13} /> Export
          </button>
        </div>

        <div className={adminCard} ref={listTopRef}>
        <BusyTable show={showSkeleton} tone="admin">
        <div className={adminTableWrap}>
          <table className="w-full min-w-[1180px] border-collapse text-sm">
            <thead>
              <tr className={adminTableHead}>
                <th className="w-10 px-4 py-3 text-center">
                  <input
                    type="checkbox"
                    checked={paginatedTickets.length > 0 && selectedIds.size > 0 && paginatedTickets.every((t) => selectedIds.has(t.id))}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th className="px-4 py-3 text-left">Ticket ID</th>
                <th className="px-4 py-3 text-left">Order ID</th>
                <th className="px-4 py-3 text-left">Event Name</th>
                <th className="px-4 py-3 text-left">Attendee</th>
                <th className="px-4 py-3 text-left">Type</th>
                <th className="px-4 py-3 text-left">Section</th>
                <th className="px-4 py-3 text-left">Row - Seat</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Scanned At</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>

            <tbody>
              {paginatedTickets.length === 0 ? (
                <tr>
                  <td colSpan="11" className={adminEmpty}>{loading ? "Loading..." : "No tickets found."}</td>
                </tr>
              ) : (
                paginatedTickets.map((ticket) => (
                  <tr key={ticket.id} className={adminRow}>
                    <td className="px-4 py-4 text-center">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(ticket.id)}
                        onChange={() => toggleOne(ticket.id)}
                      />
                    </td>
                    <td className={adminTdName}>
                      <p>{ticket.ticketId}</p>
                      <p className="text-xs font-normal text-gray-500">Manual entry if QR fails</p>
                    </td>
                    <td className={adminTd}>{ticket.orderCode}</td>
                    <td className={adminTd}>{ticket.eventTitle}</td>
                    <td className={adminTd}>{ticket.attendee}</td>
                    <td className={adminTd}>{ticket.type}</td>
                    <td className={adminTd}>{ticket.exactSection || "-"}</td>
                    <td className={adminTd}>{ticket.seatLabel || "-"}</td>
                    <td className="px-4 py-4">
                      <span className={`${adminStatusPill} ${
                        ticket.status === "Used"
                          ? "bg-green-100 text-green-700"
                          : ticket.status === "Refunded"
                            ? "bg-violet-100 text-violet-700"
                            : "bg-blue-100 text-blue-700"
                      }`}>
                        {ticket.status}
                      </span>
                    </td>
                    <td className={adminTd}>{ticket.scannedAt ? formatDateTime(ticket.scannedAt) : "Not scanned"}</td>
                    <td className="px-4 py-4 text-right">
                      <div className="flex justify-end gap-1.5">
                        <button
                          onClick={() => setSelectedTicket(ticket)}
                          className={adminPrimaryBtn}
                        >
                          <Eye size={12} /> View Details
                        </button>
                        <button
                          type="button"
                          onClick={() => openStatusConfirm("Used", [ticket.id], "one")}
                          className={adminSecondaryBtn}
                        >
                          <Check size={12} /> Mark Used
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        </BusyTable>

        <AdminPagination
          className="mt-6"
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={goToPage}
        />
        </div>

        {showExportModal && (
          <div className="fixed inset-0 z-50 bg-black/50 p-4 overflow-y-auto" onClick={closeExportModal}>
            <div
              className="mx-auto w-full max-w-md rounded-2xl border border-gray-200 bg-white shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                <div>
                  <p className="text-[11px] uppercase tracking-widest text-gray-500 font-bold">Export</p>
                  <h2 className="text-lg font-black text-gray-900 mt-0.5">Tickets Report</h2>
                </div>
                <button
                  type="button"
                  onClick={closeExportModal}
                  className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="p-5 space-y-4">
                <p className="text-xs text-gray-500">
                  This list is for one event. Date range filters when the ticket was issued, not show date. Status and search on the page still apply.
                </p>

                <div className="block text-xs text-gray-600">
                  Event
                  <SearchableSelect
                    value={exportEvent}
                    onChange={setExportEvent}
                    options={exportCandidateEvents}
                    placeholder="Select an event"
                    searchPlaceholder="Search events..."
                    emptyLabel="No events found"
                    className="mt-1 w-full"
                  />
                </div>

                <button
                  type="button"
                  onClick={() => setUseIssuedRange((prev) => !prev)}
                  className={`${adminSecondaryBtn} h-8`}
                >
                  {useIssuedRange ? "Using issued-date filter" : "Filter by issued dates"}
                </button>

                <div className="grid grid-cols-2 gap-3">
                  <label className="text-xs text-gray-600">
                    Issued from
                    <input
                      type="date"
                      value={exportFrom}
                      onChange={(e) => {
                        setUseIssuedRange(true);
                        setExportFrom(e.target.value);
                      }}
                      disabled={!useIssuedRange}
                      className="mt-1 h-9 w-full rounded-lg border border-gray-300 bg-white px-3 text-xs text-gray-700 disabled:bg-gray-100"
                    />
                  </label>
                  <label className="text-xs text-gray-600">
                    Issued to
                    <input
                      type="date"
                      value={exportTo}
                      onChange={(e) => {
                        setUseIssuedRange(true);
                        setExportTo(e.target.value);
                      }}
                      disabled={!useIssuedRange}
                      className="mt-1 h-9 w-full rounded-lg border border-gray-300 bg-white px-3 text-xs text-gray-700 disabled:bg-gray-100"
                    />
                  </label>
                </div>

                <fieldset className="space-y-2">
                  <legend className="text-xs font-semibold text-gray-700">Format</legend>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="radio"
                      name="tickets-export-format"
                      checked={exportFormat === "csv"}
                      onChange={() => setExportFormat("csv")}
                    />
                    CSV (summary + ticket rows)
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="radio"
                      name="tickets-export-format"
                      checked={exportFormat === "pdf"}
                      onChange={() => setExportFormat("pdf")}
                    />
                    PDF summary
                  </label>
                </fieldset>
              </div>

              <div className="flex gap-2 justify-end border-t border-gray-200 px-5 py-3">
                <button type="button" onClick={closeExportModal} className={adminSecondaryBtn}>
                  Cancel
                </button>
                <button type="button" onClick={downloadTicketsReport} className={adminPrimaryBtn}>
                  Download
                </button>
              </div>
            </div>
          </div>
        )}

        {statusConfirm && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="w-full max-w-[92vw] sm:max-w-md max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-2xl border border-gray-100 p-6">
              <h3 className="text-base font-black text-gray-900 uppercase tracking-tight mb-2">
                {statusConfirm.status === "Used" ? "Mark used?" : "Mark unused?"}
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                {statusConfirm.status === "Used"
                  ? "Do you want to mark the selected tickets as used?"
                  : "Do you want to mark the selected tickets as unused?"}
              </p>
              <div className="max-h-40 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                {confirmTicketIds.length > 0 ? (
                  <ul className="space-y-1">
                    {confirmTicketIds.map((ticketId) => (
                      <li key={ticketId} className="text-xs font-semibold text-gray-800">
                        {ticketId}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-gray-500">No ticket IDs selected.</p>
                )}
              </div>
              <div className="mt-6 flex flex-wrap justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setStatusConfirm(null)}
                  disabled={isUpdatingStatus}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-bold hover:bg-gray-50 disabled:opacity-60"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmStatusChange}
                  disabled={isUpdatingStatus || !statusConfirm.ids.length}
                  className="px-4 py-2 rounded-lg bg-brand-maroon text-white text-sm font-bold hover:bg-red-700 disabled:opacity-60"
                >
                  {isUpdatingStatus
                    ? "Updating..."
                    : statusConfirm.status === "Used"
                      ? "Yes, mark used"
                      : "Yes, mark unused"}
                </button>
              </div>
            </div>
          </div>
        )}

        {selectedTicket && (
          <div className="fixed inset-0 z-50 bg-black/50 p-4 overflow-y-auto" onClick={() => setSelectedTicket(null)}>
            <div
              className="mx-auto mt-8 w-full max-w-[92vw] sm:max-w-xl max-h-[90vh] overflow-y-auto rounded-2xl border border-gray-700 bg-[#111111] shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
                <div>
                  <p className="text-[11px] uppercase tracking-widest text-white/60 font-bold">Ticket Receipt</p>
                  <h3 className="text-lg font-black text-white">{selectedTicket.ticketId}</h3>
                </div>
                <button onClick={() => setSelectedTicket(null)} className="p-2 rounded-lg hover:bg-white/10 text-white/80">
                  <X size={18} />
                </button>
              </div>

              <div className="px-5 pb-5 pt-6 flex flex-col items-center">
                <TicketReceiptCard ticket={selectedTicket} />

                <div className="w-full mt-4 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-[11px] text-white/70">
                  Attendee: <span className="font-semibold text-white">{selectedTicket.attendee}</span>
                  {selectedTicket.orderCode ? ` • ${selectedTicket.orderCode}` : ""}
                </div>
                <div className="w-full mt-2 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-[11px] text-white/70">
                  Status: <span className="font-semibold text-white">{selectedTicket.status}</span>
                  {selectedTicket.scannedAt ? ` • Scanned: ${formatDateTime(selectedTicket.scannedAt)}` : " • Not scanned"}
                </div>

                <button
                  onClick={handleDownloadReceipt}
                  className="w-full mt-4 bg-brand-maroon hover:bg-red-700 text-white rounded-lg py-2.5 text-[11px] font-black uppercase tracking-widest"
                >
                  Download Receipt PDF
                </button>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default Tickets;
